import * as React from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';
import { colors, chartColorArray, chartColors } from '@/lib/colors';
import { formatCurrencyDisplay } from '@/lib/utils';
import type { BreakdownTimeSeriesDataPoint } from '@/data/types/breakdown.types';
import { format, eachDayOfInterval, eachMonthOfInterval, startOfDay, startOfMonth } from 'date-fns';
import { type DateRange } from 'react-day-picker';
import type { GranularityValue } from '@/components/GranularitySelector';
import { DashboardEmptyState } from '@/components/Dashboard/EmptyState';

// Custom label component for the reference line
const ReferenceLineLabel = ({ viewBox, value }: { viewBox?: { x: number; y: number }; value: string }) => {
  if (!viewBox) return null;
  return (
    <text
      x={viewBox.x + 4}
      y={viewBox.y - 8}
      fill={colors.teal[500]}
      fontSize={14}
      fontWeight={600}
      stroke="none"
    >
      {value}
    </text>
  );
};

interface LastXDaysSpendProps {
  data: BreakdownTimeSeriesDataPoint[];
  groupBy?: string;
  granularity?: GranularityValue;
  dateRange?: DateRange;
  isLoading?: boolean;
}

// Calculate the maximum stacked bar value
const getMaxTotal = (chartData: any[]) => {
  if (chartData.length === 0) return 0;
  return Math.max(
    ...chartData.map((item) => {
      const keys = Object.keys(item).filter(key => key !== 'timestamp' && key !== 'date' && key !== 'dateLabel' && key !== 'dayLabel');
      return keys.reduce((sum, key) => sum + (typeof item[key] === 'number' ? item[key] : 0), 0);
    })
  );
};

// Format currency value with small value handling
const formatCurrency = (value: number): string => {
  // Handle small values (> 0 but < 0.01)
  if (value > 0 && value < 0.01) {
    const { fullValue } = formatCurrencyDisplay(value);
    return fullValue;
  }
  // Large values get "k" suffix
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(1)}k`;
  }
  // Normal values
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Format date for tooltip based on granularity
const formatDateForTooltip = (dateStr: string, granularity: GranularityValue) => {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;

  switch (granularity) {
    case 'hourly':
      return `Hour: ${format(date, 'HH:00')}`;
    case 'daily':
      return format(date, 'MMM d, yyyy');
    case 'monthly':
      return format(date, 'MMM yyyy');
    default:
      return format(date, 'MMM d, yyyy');
  }
};

// Generate all expected time periods based on granularity and date range
const generateTimePeriods = (
  granularity: GranularityValue,
  dateRange?: DateRange
): Date[] => {
  if (!dateRange?.from || !dateRange?.to) return [];

  const start = new Date(dateRange.from);
  const end = new Date(dateRange.to);

  switch (granularity) {
    case 'hourly': {
      // For hourly, show only 0-23 hours (aggregated across days)
      const hours: Date[] = [];
      const baseDate = new Date();
      baseDate.setHours(0, 0, 0, 0);
      for (let i = 0; i < 24; i++) {
        const d = new Date(baseDate);
        d.setHours(i);
        hours.push(d);
      }
      return hours;
    }
    case 'daily': {
      return eachDayOfInterval({ start: startOfDay(start), end: startOfDay(end) });
    }
    case 'monthly': {
      return eachMonthOfInterval({ start: startOfMonth(start), end: startOfMonth(end) });
    }
    default:
      return eachDayOfInterval({ start: startOfDay(start), end: startOfDay(end) });
  }
};

// Format date label based on granularity
const formatDateLabel = (date: Date, granularity: GranularityValue): string => {
  switch (granularity) {
    case 'hourly':
      return format(date, 'HH:mm');
    case 'daily':
      return format(date, 'MMM d');
    case 'monthly':
      return format(date, 'MMM yyyy');
    default:
      return format(date, 'MMM d');
  }
};

// Get a key for matching time periods
const getTimePeriodKey = (date: Date, granularity: GranularityValue): string => {
  switch (granularity) {
    case 'hourly':
      return format(date, 'HH:00');
    case 'daily':
      return format(startOfDay(date), 'yyyy-MM-dd');
    case 'monthly':
      return format(startOfMonth(date), 'yyyy-MM');
    default:
      return format(startOfDay(date), 'yyyy-MM-dd');
  }
};

// Get top 10 groups and identify "Other"
const getTopGroups = (apiData: BreakdownTimeSeriesDataPoint[]) => {
  const groupTotals: Record<string, number> = {};

  apiData.forEach((point) => {
    // Check if the point has a 'group' field (the actual API structure)
    if (point.group && typeof point.group === 'string' && typeof point.cost === 'number') {
      groupTotals[point.group] = (groupTotals[point.group] || 0) + point.cost;
    }
    // Fallback: handle dynamic key structure
    else {
      Object.keys(point).forEach((key) => {
        if (key !== 'timestamp' && typeof point[key] === 'number') {
          groupTotals[key] = (groupTotals[key] || 0) + (point[key] as number);
        }
      });
    }
  });

  const sortedGroups = Object.entries(groupTotals)
    .sort(([, a], [, b]) => b - a)
    .map(([name]) => name);

  const top10 = sortedGroups.slice(0, 10);
  const remaining = sortedGroups.slice(10);

  return { top10, hasOther: remaining.length > 0 };
};

// Transform API data to chart format and handle grouping
const transformData = (
  apiData: BreakdownTimeSeriesDataPoint[],
  granularity: GranularityValue,
  dateRange?: DateRange
): Array<{
  date: string;
  dateLabel: string;
  timestamp: string;
  [key: string]: string | number;
}> => {
  const { top10, hasOther } = getTopGroups(apiData);
  const groupKeys = [...top10];
  if (hasOther) groupKeys.push('Other');

  // Group API data by time period key, aggregating multiple points per timestamp
  const dataMap = new Map<string, Record<string, number>>();
  apiData.forEach((point) => {
    if (!point.timestamp) return;
    const date = new Date(point.timestamp);
    if (isNaN(date.getTime())) return;
    const periodKey = getTimePeriodKey(date, granularity);

    if (!dataMap.has(periodKey)) {
      dataMap.set(periodKey, {});
    }
    const periodData = dataMap.get(periodKey)!;

    // Handle the actual API structure: { timestamp, group, cost }
    if (point.group && typeof point.group === 'string' && typeof point.cost === 'number') {
      const groupName = top10.includes(point.group) ? point.group : 'Other';
      periodData[groupName] = (periodData[groupName] || 0) + point.cost;
    }
    // Fallback: handle dynamic key structure
    else {
      Object.keys(point).forEach((key) => {
        if (key !== 'timestamp' && typeof point[key] === 'number') {
          const groupName = top10.includes(key) ? key : 'Other';
          periodData[groupName] = (periodData[groupName] || 0) + (point[key] as number);
        }
      });
    }
  });

  // Generate all expected time periods
  const timePeriods = generateTimePeriods(granularity, dateRange);

  // Transform to chart data format
  return timePeriods.map((period) => {
    const periodKey = getTimePeriodKey(period, granularity);
    const periodData = dataMap.get(periodKey) || {};

    const transformed: any = {
      date: periodKey,
      dateLabel: formatDateLabel(period, granularity),
      timestamp: period.toISOString(),
    };

    groupKeys.forEach((groupKey) => {
      transformed[groupKey] = periodData[groupKey] || 0;
    });

    return transformed;
  });
};

// Get all distinct group labels from the data
const getGroupLabels = (
  apiData: BreakdownTimeSeriesDataPoint[]
): string[] => {
  if (!apiData || apiData.length === 0) return [];

  const { top10, hasOther } = getTopGroups(apiData);
  const groupLabels = [...top10];
  if (hasOther) groupLabels.push('Other');

  return groupLabels;
};

const CustomTooltip = ({ active, payload, granularity }: any) => {
  if (active && payload && payload.length) {
    const total = payload.reduce((sum: number, entry: any) => sum + (entry.value || 0), 0);
    const dateStr = payload[0].payload.timestamp || payload[0].payload.date;
    return (
      <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
        <p className="text-sm font-medium mb-2">{formatDateForTooltip(dateStr, granularity)}</p>
        {payload
          .filter((entry: any) => entry.value > 0) // Only show groups with values > 0
          .map((entry: any, index: number) => {
            const label = entry.name || entry.dataKey;
            return (
              <p key={index} className="text-xs" style={{ color: entry.color }}>
                {label}: {formatCurrency(entry.value)}
              </p>
            );
          })}
        <p className="text-xs font-medium mt-1 pt-1 border-t border-border">
          Total: {formatCurrency(total)}
        </p>
      </div>
    );
  }
  return null;
};

// Get color for a group by name/index
const getGroupColor = (groupName: string, index: number): string => {
  if (groupName === 'Other') {
    return chartColors.gray;
  }
  return chartColorArray[index % chartColorArray.length];
};

// Custom X-axis tick that shows labels intelligently based on data density
const CustomXAxisTick = ({ x, y, payload, index, visibleTicksCount, dataLength }: any) => {
  const isFirst = index === 0;
  const isLast = index === visibleTicksCount - 1;

  // For small datasets (<= 12 items), show all labels
  // For larger datasets, show first, last, and some middle ones
  if (dataLength <= 12) {
    // Show all labels for small datasets
    return (
      <text
        x={x}
        y={y + 12}
        fill="#71717a"
        fontSize={12}
        fontFamily="Jost, sans-serif"
        textAnchor="middle"
        stroke="none"
      >
        {payload.value}
      </text>
    );
  }

  // For larger datasets, show first, last, and evenly spaced middle labels
  const showLabel = isFirst || isLast || (index % Math.ceil(dataLength / 4) === 0);

  if (!showLabel) return null;

  return (
    <text
      x={isFirst ? x - 8 : isLast ? x + 5 : x}
      y={y + 12}
      fill="#71717a"
      fontSize={12}
      fontFamily="Jost, sans-serif"
      textAnchor={isFirst ? 'start' : isLast ? 'end' : 'middle'}
      stroke="none"
    >
      {payload.value}
    </text>
  );
};

const LastXDaysSpend: React.FC<LastXDaysSpendProps> = ({
  data,
  granularity = 'daily',
  dateRange,
  isLoading = false
}) => {
  const transformedData = React.useMemo(
    () => transformData(data, granularity, dateRange),
    [data, granularity, dateRange]
  );
  const groupLabels = React.useMemo(() => getGroupLabels(data), [data]);
  const maxTotal = React.useMemo(() => getMaxTotal(transformedData), [transformedData]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center" style={{ height: '330px' }}>
        <div className="text-sm text-[#8A8F98]">Loading chart data...</div>
      </div>
    );
  }

  // Check if all values are effectively zero/empty
  const hasData = transformedData.length > 0 && transformedData.some(item => {
    const keys = Object.keys(item).filter(key => key !== 'timestamp' && key !== 'date' && key !== 'dateLabel' && key !== 'dayLabel');
    return keys.some(key => typeof item[key] === 'number' && item[key] > 0);
  });

  if (!hasData) {
    return <DashboardEmptyState height={330} />;
  }

  // Determine which bar should have rounded corners (the last one)
  const lastGroupIndex = groupLabels.length - 1;

  return (
    <div className="space-y-4">
      {/* Chart */}
      <div className="w-full" style={{ height: '330px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={transformedData}
            margin={{
              top: 40,
              right: 0,
              left: 0,
              bottom: 0,
            }}
            style={{ stroke: '#fff', strokeWidth: 1 }}
          >
            <XAxis
              dataKey="dateLabel"
              axisLine={false}
              tickLine={false}
              tick={(props) => <CustomXAxisTick {...props} dataLength={transformedData.length} />}
              interval={0}
            />
            <YAxis
              domain={[0, maxTotal]}
              axisLine={false}
              tickLine={false}
              hide={true}
            />
            <ReferenceLine
              y={maxTotal}
              stroke={colors.teal[500]}
              strokeDasharray="4 4"
              strokeWidth={1}
              label={<ReferenceLineLabel value={formatCurrency(maxTotal)} />}
              isFront={false}
            />
            <Tooltip content={<CustomTooltip granularity={granularity} />} cursor={{ fill: colors.purple[25], opacity: 1 }} />
            {groupLabels.map((groupKey, index) => {
              const isLast = index === lastGroupIndex;
              const color = getGroupColor(groupKey, index);
              return (
                <Bar
                  key={groupKey}
                  dataKey={groupKey}
                  name={groupKey}
                  stackId="a"
                  fill={color}
                  radius={isLast ? [3, 3, 0, 0] : [0, 0, 0, 0]}
                  maxBarSize={40}
                >
                  {transformedData.map((_, dataIndex) => (
                    <Cell
                      key={`cell-${groupKey}-${dataIndex}`}
                      fill={color}
                    />
                  ))}
                </Bar>
              );
            })}
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-x-4 gap-y-2 flex-wrap pb-2">
        {groupLabels.map((groupKey, index) => {
          const color = getGroupColor(groupKey, index);
          return (
            <div key={groupKey} className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: color }} />
              <span className="text-xs text-muted-foreground">{groupKey}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export { LastXDaysSpend };
